import { api } from './api';

export const directSshPath = ({ organizationId, projectId, resourceId }) => (
  `/v1/organizations/${encodeURIComponent(organizationId)}`
  + `/projects/${encodeURIComponent(projectId)}`
  + `/resources/${encodeURIComponent(resourceId)}/ssh-access`
);

const idempotencyKey = (resourceId, action) => {
  const key = `cloudwatcher:ssh:${resourceId}:${action}`;
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const created = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.sessionStorage.setItem(key, created);
  return created;
};

export const newDirectSshIntentKey = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const directSshApi = {
  getStatus: (scope) => api.get(directSshPath(scope)),
  changeTrust: (scope, action, request) => api.post(
    `${directSshPath(scope)}/trust/${action}`,
    action === 'enroll'
      ? { allowed_principals: request.allowedPrincipals, reason: request.reason }
      : { expected_revision: request.expectedRevision, reason: request.reason },
    { headers: { 'Idempotency-Key': idempotencyKey(scope.resourceId, action === 'enroll' ? `trust:enroll:${[...request.allowedPrincipals].sort().join(',')}` : `trust:${action}:${request.expectedRevision ?? 0}`) } },
  ),
  getBootstrap: (scope, expectedRevision) => api.post(
    `${directSshPath(scope)}/trust/bootstrap`,
    { expected_revision: expectedRevision },
  ),
  issueCertificate: (scope, request, intentKey) => api.post(
    `${directSshPath(scope)}/certificates`,
    request,
    { headers: { 'Idempotency-Key': intentKey } },
  ),
};

export const normalizeDirectSshStatus = (payload = {}) => {
  const trust = payload.enrollment || payload.trust || payload.server_trust;
  const trustRecord = trust || {};
  const activeCa = (trustRecord.trusted_ca_keys || []).find((key) => key.state === 'active') || trustRecord.trusted_ca_keys?.[0] || {};
  const capabilities = payload.capabilities || {};
  const access = capabilities.issue_certificate || capabilities.request_certificate || payload.certificate_access || {};
  const management = capabilities.manage_trust || payload.trust_management || {};
  return {
    feature_enabled: payload.feature_enabled !== false,
    trust: {
      status: trust ? trustRecord.status || 'unknown' : 'not_enrolled',
      revision: trustRecord.revision ?? 0,
      signer_key_id: trustRecord.signer_key_id || activeCa.key_id,
      ca_fingerprint: trustRecord.ca_fingerprint || trustRecord.fingerprint || activeCa.fingerprint,
      enrolled_at: trustRecord.enrolled_at || trustRecord.created_at,
      rotated_at: trustRecord.rotated_at,
      observed_at: trustRecord.observed_at || trustRecord.updated_at,
      last_error: trustRecord.last_error,
      allowed_principals: trustRecord.allowed_principals || [],
      trust_generation: trustRecord.trust_generation,
    },
    can_manage_trust: management.allowed === true || payload.can_manage_trust === true,
    manage_reason: management.reason_code || management.reason || management.message || payload.manage_reason,
    can_issue: access.allowed === true || payload.can_request_certificate === true,
    issue_reason: access.reason_code || access.reason || access.message || payload.issue_reason,
    target: {
      host: payload.target?.host,
      port: Number(payload.target?.port || 22),
      principals: payload.target?.principals || trustRecord.allowed_principals || payload.allowed_principals || [],
      host_fingerprint: payload.target?.host_fingerprint,
    },
    eligible_grants: (payload.eligible_grants || payload.certificate_access?.eligible_grants || []).map((grant) => ({
      id: grant.id || grant.grant_id,
      name: grant.name || grant.reason || 'SSH access grant',
      expires_at: grant.expires_at,
      principals: grant.principals || grant.allowed_principals || [],
    })),
    max_ttl_seconds: Math.min(Number(payload.max_ttl_seconds || payload.policy?.max_ttl_seconds || 900), 900),
  };
};

export const normalizeDirectSshMutation = (payload = {}) => {
  const serviceEnvelope = payload.enrollment?.enrollment ? payload.enrollment : payload;
  const enrollment = serviceEnvelope.enrollment || payload.enrollment || null;
  return {
    status: normalizeDirectSshStatus({ ...payload, enrollment }),
    bootstrap: serviceEnvelope.bootstrap || payload.bootstrap || null,
  };
};

export const activeSshDevices = (identity) => (identity?.device_keys || []).filter(
  (device) => ['active', 'verified'].includes(device.status) && !device.revoked_at,
);

export const activeSshGrants = (grants, now = new Date()) => (grants || []).filter((grant) => {
  const expiry = new Date(grant.expires_at);
  return grant.id && !Number.isNaN(expiry.getTime()) && expiry > now;
});

export const trustPresentation = (status) => ({
  active: { label: 'Trusted', tone: 'emerald', detail: 'This server confirmed the active Cloud Watcher SSH CA generation.' },
  enrolled: { label: 'Trusted', tone: 'emerald', detail: 'This server confirmed the active Cloud Watcher SSH CA generation.' },
  not_enrolled: { label: 'Not enrolled', tone: 'zinc', detail: 'This server does not yet trust Cloud Watcher certificates.' },
  unenrolled: { label: 'Not enrolled', tone: 'zinc', detail: 'This server does not trust Cloud Watcher certificates.' },
  enrolling: { label: 'Enrollment pending', tone: 'amber', detail: 'Cloud Watcher has not yet confirmed CA trust on the server.' },
  pending_installation: { label: 'Installation pending', tone: 'amber', detail: 'Install the one-use bootstrap package on the server. Certificate issuance remains blocked until confirmation.' },
  rotating: { label: 'Rotation pending', tone: 'amber', detail: 'The new CA is not authoritative until the server confirms it.' },
  pending_rotation: { label: 'Rotation pending', tone: 'amber', detail: 'Install the new trust generation. The old generation remains authoritative until confirmation.' },
  unenrolling: { label: 'Removal pending', tone: 'amber', detail: 'Treat Cloud Watcher certificates as trusted until removal is confirmed.' },
  pending_removal: { label: 'Removal pending', tone: 'amber', detail: 'Cloud Watcher certificates remain trusted until server-side removal is confirmed.' },
  drifted: { label: 'Trust changed', tone: 'red', detail: 'Observed server trust differs from the expected CA. Direct SSH issuance is blocked.' },
  error: { label: 'Enrollment error', tone: 'red', detail: 'Cloud Watcher could not confirm the server trust configuration.' },
  unsupported: { label: 'Unavailable', tone: 'zinc', detail: 'This server cannot be enrolled automatically.' },
  disabled: { label: 'Disabled', tone: 'zinc', detail: 'Managed SSH access is disabled by the operator.' },
  unknown: { label: 'Unknown', tone: 'red', detail: 'Server trust could not be verified. Certificate issuance is blocked.' },
}[status] || { label: 'Unknown', tone: 'red', detail: 'Server trust could not be verified. Certificate issuance is blocked.' });

export const isTrustReady = (status) => ['active', 'enrolled'].includes(status);

export const capabilityDenialCopy = (reason, fallback) => ({
  project_admin_required: 'A project or global administrator manages server trust.',
  server_trust_not_confirmed: 'Finish and confirm the server trust installation before requesting a certificate.',
  terminal_grant_required: 'You need an active terminal access grant for this server.',
  terminal_access_denied: 'Your current role or access policy does not allow direct SSH to this server.',
  server_target_unavailable: 'This server does not have a usable SSH host configured yet.',
}[reason] || fallback);

export const certificateState = (certificate, now = new Date()) => {
  if (!certificate) return 'none';
  if (certificate.revocation_status === 'revoked') return 'revoked';
  if (certificate.revocation_status === 'pending') return 'revocation_pending';
  const expiry = new Date(certificate.expires_at || certificate.valid_before);
  if (Number.isNaN(expiry.getTime())) return 'invalid';
  return expiry <= now ? 'expired' : 'active';
};

export const remainingTime = (value, now = new Date()) => {
  const seconds = Math.max(0, Math.floor((new Date(value).getTime() - now.getTime()) / 1000));
  if (seconds <= 0) return 'Expired';
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')} remaining`;
};

const shellQuote = (value) => `'${String(value).replace(/'/g, `'"'"'`)}'`;
const shellPathQuote = (value) => String(value).startsWith('~/')
  ? `"$HOME"/${shellQuote(String(value).slice(2))}`
  : shellQuote(value);

export const certificateFilename = (resourceId, deviceId) => {
  const safe = (value) => String(value || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 32) || 'ssh';
  return `cloudwatcher-${safe(resourceId)}-${safe(deviceId)}-cert.pub`;
};

export const buildDirectSshCommand = ({ host, port, principal, privateKeyPath, certificatePath }) => {
  if (!host || !principal || !privateKeyPath || !certificatePath) return '';
  const safePort = Number.isInteger(Number(port)) && Number(port) >= 1 && Number(port) <= 65535 ? Number(port) : 22;
  return `ssh -i ${shellPathQuote(privateKeyPath)} -o CertificateFile=${shellPathQuote(certificatePath)} -p ${safePort} ${shellQuote(`${principal}@${host}`)}`;
};

export const bootstrapScriptFilename = (resourceId, generation) => {
  const safe = (value) => String(value || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 32) || 'ssh';
  return `cloudwatcher-enroll-${safe(resourceId)}-${safe(generation)}.sh`;
};

const confirmationUrl = () => {
  const backend = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
  return `${backend}/api/v1/ssh-enrollment-confirmations`;
};

export const buildBootstrapScript = ({ bootstrap, action = 'install' }) => {
  const keys = (bootstrap?.trusted_ca_keys || []).filter(
    (key) => ['active', 'retiring'].includes(key.state) && /^(ssh-ed25519|ssh-rsa) [A-Za-z0-9+/]+={0,2}$/.test(key.public_key || ''),
  );
  if (!bootstrap?.token || !bootstrap?.trust_generation || (action !== 'remove' && !keys.length)) return '';
  const installedFingerprints = action === 'remove' ? [] : keys.map((key) => key.fingerprint).sort();
  const payload = JSON.stringify({
    token: bootstrap.token,
    trust_generation: bootstrap.trust_generation,
    installed_fingerprints: installedFingerprints,
  });
  const keyLines = keys.map((key) => key.public_key).join('\n');
  const candidate = action === 'remove' ? `
rm -f "$CW_KEYS" "$CW_CONFIG"
` : `
cat > "$BACKUP_DIR/candidate-ca-keys.pub" <<'CLOUDWATCHER_CA_KEYS'
${keyLines}
CLOUDWATCHER_CA_KEYS
chmod 0644 "$BACKUP_DIR/candidate-ca-keys.pub"
cat > "$BACKUP_DIR/candidate-sshd.conf" <<'CLOUDWATCHER_SSHD_CONFIG'
# Managed by Cloud Watcher. Public CA keys only.
TrustedUserCAKeys /etc/ssh/cloudwatcher/trusted-user-ca-keys.pub
CLOUDWATCHER_SSHD_CONFIG
chmod 0644 "$BACKUP_DIR/candidate-sshd.conf"
mv -f "$BACKUP_DIR/candidate-ca-keys.pub" "$CW_KEYS"
mv -f "$BACKUP_DIR/candidate-sshd.conf" "$CW_CONFIG"
`;
  return `#!/bin/sh
set -eu
umask 077
[ "$(id -u)" -eq 0 ] || { echo "Run this one-use bootstrap as root." >&2; exit 1; }
if command -v sshd >/dev/null 2>&1; then
  SSHD_BIN=$(command -v sshd)
elif [ -x /usr/sbin/sshd ]; then
  SSHD_BIN=/usr/sbin/sshd
else
  echo "OpenSSH server is not installed." >&2
  exit 1
fi
command -v ssh-keygen >/dev/null 2>&1 || { echo "OpenSSH key tools are not installed." >&2; exit 1; }
command -v curl >/dev/null 2>&1 || { echo "curl is required for one-use confirmation." >&2; exit 1; }
CW_KEYS=/etc/ssh/cloudwatcher/trusted-user-ca-keys.pub
CW_CONFIG=/etc/ssh/sshd_config.d/90-cloudwatcher-ca.conf
install -d -m 0755 /etc/ssh/cloudwatcher /etc/ssh/sshd_config.d
BACKUP_DIR=$(mktemp -d /etc/ssh/.cloudwatcher-rollback.XXXXXX)
chmod 0700 "$BACKUP_DIR"

backup_file() {
  if [ -e "$1" ]; then cp -p "$1" "$BACKUP_DIR/$2"; else : > "$BACKUP_DIR/$2.absent"; fi
}
restore_file() {
  if [ -e "$BACKUP_DIR/$2.absent" ]; then
    rm -f "$1"
  elif [ -e "$BACKUP_DIR/$2" ]; then
    cp -p "$BACKUP_DIR/$2" "$1.rollback"
    mv -f "$1.rollback" "$1"
  fi
}
reload_sshd() {
  if command -v systemctl >/dev/null 2>&1; then
    systemctl reload sshd 2>/dev/null || systemctl reload ssh
  elif command -v service >/dev/null 2>&1; then
    service sshd reload 2>/dev/null || service ssh reload
  else
    return 1
  fi
}
rollback() {
  restore_file "$CW_KEYS" ca-keys
  restore_file "$CW_CONFIG" sshd-config
  "$SSHD_BIN" -t >/dev/null 2>&1 && reload_sshd >/dev/null 2>&1 || true
  rm -rf "$BACKUP_DIR"
}

backup_file "$CW_KEYS" ca-keys
backup_file "$CW_CONFIG" sshd-config
trap rollback EXIT
${candidate}
"$SSHD_BIN" -t
${action === 'remove' ? `
if "$SSHD_BIN" -T | grep -Fq '/etc/ssh/cloudwatcher/trusted-user-ca-keys.pub'; then
  echo "Cloud Watcher CA trust is still effective; refusing false confirmation." >&2
  exit 1
fi
` : `
"$SSHD_BIN" -T | grep -Fq 'trustedusercakeys /etc/ssh/cloudwatcher/trusted-user-ca-keys.pub' || {
  echo "sshd does not report the Cloud Watcher CA file as effective; refusing confirmation." >&2
  exit 1
}
EXPECTED_FINGERPRINTS=$(cat <<'CLOUDWATCHER_EXPECTED_FINGERPRINTS'
${installedFingerprints.join('\n')}
CLOUDWATCHER_EXPECTED_FINGERPRINTS
)
OBSERVED_FINGERPRINTS=$(ssh-keygen -lf /etc/ssh/cloudwatcher/trusted-user-ca-keys.pub | awk '{print $2}' | sort)
[ "$OBSERVED_FINGERPRINTS" = "$EXPECTED_FINGERPRINTS" ] || {
  echo "Installed CA fingerprints do not match the enrollment package." >&2
  exit 1
}
`}
if ! reload_sshd; then
  echo "Could not safely reload sshd; trust remains unconfirmed." >&2
  exit 1
fi
curl --fail --silent --show-error --connect-timeout 5 --max-time 15 \\
  -H 'Content-Type: application/json' --data-binary @- ${shellQuote(confirmationUrl())} <<'CLOUDWATCHER_CONFIRMATION'
${payload}
CLOUDWATCHER_CONFIRMATION
trap - EXIT
rm -rf "$BACKUP_DIR"
rm -f -- "$0"
echo "Cloud Watcher SSH trust confirmed. This one-use script deleted itself."
`;
};
